import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, ChevronDown, GripVertical, Mic, Square } from 'lucide-react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';

interface Message {
  role: 'user' | 'model';
  text: string;
  timestamp: Date;
}

interface AgentConfig {
  agentName: string;
  agentPersonality: string;
}

interface ChatGeometry {
  right: number;
  bottom: number;
  width: number;
  height: number;
}

const GEOMETRY_STORAGE_KEY = 'qpulse_chat_geometry';
const DEFAULT_GEOMETRY: ChatGeometry = { right: 24, bottom: 24, width: 384, height: 580 };
const MIN_WIDTH = 320;
const MIN_HEIGHT = 420;

function clampGeometry(geo: ChatGeometry): ChatGeometry {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxWidth = Math.min(720, viewportWidth - 32);
  const maxHeight = Math.min(800, viewportHeight - 96);
  const width = Math.min(Math.max(geo.width, MIN_WIDTH), maxWidth);
  const height = Math.min(Math.max(geo.height, MIN_HEIGHT), maxHeight);
  const right = Math.min(Math.max(geo.right, -(width - 40)), viewportWidth - 40);
  const bottom = Math.min(Math.max(geo.bottom, -(height - 40)), viewportHeight - 40);
  return { right, bottom, width, height };
}

function loadStoredGeometry(): ChatGeometry {
  try {
    const raw = localStorage.getItem(GEOMETRY_STORAGE_KEY);
    if (!raw) return DEFAULT_GEOMETRY;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed.right === 'number' && typeof parsed.bottom === 'number' &&
      typeof parsed.width === 'number' && typeof parsed.height === 'number'
    ) {
      return clampGeometry(parsed);
    }
  } catch (_e) { /* malformed or unavailable storage - ignore */ }
  return DEFAULT_GEOMETRY;
}

function saveGeometryToStorage(geo: ChatGeometry) {
  try {
    localStorage.setItem(GEOMETRY_STORAGE_KEY, JSON.stringify(geo));
  } catch (_e) { /* storage unavailable (private mode, quota) - ignore */ }
}

export function AIChatBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agentConfig, setAgentConfig] = useState<AgentConfig>({ agentName: 'QPulse Assistant', agentPersonality: '' });
  const [configLoaded, setConfigLoaded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [geometry, setGeometry] = useState<ChatGeometry>(() => loadStoredGeometry());
  const geometryRef = useRef(geometry);
  geometryRef.current = geometry;

  const [isDragging, setIsDragging] = useState(false);
  const dragOrigin = useRef<{ startClientX: number; startClientY: number; startRight: number; startBottom: number } | null>(null);

  const [isResizing, setIsResizing] = useState(false);
  const resizeOrigin = useRef<{ startClientX: number; startClientY: number; startWidth: number; startHeight: number } | null>(null);

  const [isTranscribing, setIsTranscribing] = useState(false);
  const audioRecorder = useAudioRecorder();

  // audioRecorder.start() sets its error state asynchronously inside the hook;
  // reading audioRecorder.error right after `await audioRecorder.start()` in
  // handleMicClick would see a stale closure over the pre-call render's object,
  // not the freshly-set error. React to the error via an effect instead so the
  // message is appended once the hook's state actually updates.
  useEffect(() => {
    if (audioRecorder.error) {
      setMessages(prev => [...prev, {
        role: 'model',
        text: audioRecorder.error as string,
        timestamp: new Date(),
      }]);
    }
  }, [audioRecorder.error]);

  const handleHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    dragOrigin.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startRight: geometry.right,
      startBottom: geometry.bottom,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e: PointerEvent) => {
      const origin = dragOrigin.current;
      if (!origin) return;
      const deltaX = e.clientX - origin.startClientX;
      const deltaY = e.clientY - origin.startClientY;
      setGeometry(clampGeometry({
        ...geometryRef.current,
        right: origin.startRight - deltaX,
        bottom: origin.startBottom - deltaY,
      }));
    };
    const handleUp = () => {
      setIsDragging(false);
      dragOrigin.current = null;
      saveGeometryToStorage(geometryRef.current);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [isDragging]);

  const handleResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    resizeOrigin.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startWidth: geometry.width,
      startHeight: geometry.height,
    };
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;
    const handleMove = (e: PointerEvent) => {
      const origin = resizeOrigin.current;
      if (!origin) return;
      const deltaX = e.clientX - origin.startClientX;
      const deltaY = e.clientY - origin.startClientY;
      setGeometry(clampGeometry({
        ...geometryRef.current,
        width: origin.startWidth - deltaX,
        height: origin.startHeight - deltaY,
      }));
    };
    const handleUp = () => {
      setIsResizing(false);
      resizeOrigin.current = null;
      saveGeometryToStorage(geometryRef.current);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [isResizing]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const loadConfig = useCallback(async () => {
    if (configLoaded) return;
    try {
      const res = await fetch('/api/agent/config', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAgentConfig(data);
      }
    } catch (_e) { /* ignore */ }
    setConfigLoaded(true);
  }, [configLoaded]);

  const handleOpen = () => {
    setGeometry((prev) => clampGeometry(prev));
    setIsOpen(true);
    loadConfig();
    if (messages.length === 0) {
      setMessages([{
        role: 'model',
        text: `¡Hola! Soy **${agentConfig.agentName}**, tu asistente de QPulseMES.\n\nPuedo ayudarte con:\n- 📊 **Dashboard e Indicadores**: cómo se calculan los KPIs y de dónde vienen los datos\n- 🏭 **Módulos del sistema**: Órdenes, Planta, Kanban, Gantt, Catálogos, y más\n- 🔐 **Permisos y usuarios**: RBAC, roles, configuración\n- 📋 **Procedimientos**: cómo realizar cualquier operación en el sistema\n\n¿En qué puedo ayudarte?`,
        timestamp: new Date(),
      }]);
    }
  };

  useEffect(() => {
    if (configLoaded && messages.length > 0 && messages[0].role === 'model') {
      setMessages(prev => [{
        ...prev[0],
        text: `¡Hola! Soy **${agentConfig.agentName}**, tu asistente de QPulseMES.\n\nPuedo ayudarte con:\n- 📊 **Dashboard e Indicadores**: cómo se calculan los KPIs y de dónde vienen los datos\n- 🏭 **Módulos del sistema**: Órdenes, Planta, Kanban, Gantt, Catálogos, y más\n- 🔐 **Permisos y usuarios**: RBAC, roles, configuración\n- 📋 **Procedimientos**: cómo realizar cualquier operación en el sistema\n\n¿En qué puedo ayudarte?`,
      }, ...prev.slice(1)]);
    }
  }, [agentConfig.agentName, configLoaded]);

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isLoading) return;

    const userMsg: Message = { role: 'user', text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const history = messages
        .filter(m => m.role === 'user' || m.role === 'model')
        .slice(-10)
        .map(m => ({ role: m.role, text: m.text }));

      const res = await fetch('/api/agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, history }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          role: 'model',
          text: data.reply || 'No se pudo obtener una respuesta.',
          timestamp: new Date(),
        }]);
      } else if (res.status === 429) {
        setMessages(prev => [...prev, {
          role: 'model',
          text: '⏳ El servicio de IA alcanzó su límite de solicitudes por minuto. Espera unos segundos e intenta de nuevo.',
          timestamp: new Date(),
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'model',
          text: 'Ocurrió un error al procesar tu pregunta. Por favor intenta de nuevo.',
          timestamp: new Date(),
        }]);
      }
    } catch (_e) {
      setMessages(prev => [...prev, {
        role: 'model',
        text: 'Error de conexión. Verifica tu red e intenta de nuevo.',
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleMicClick = async () => {
    if (audioRecorder.state === 'recording') {
      const result = await audioRecorder.stop();
      if (!result) {
        setMessages(prev => [...prev, {
          role: 'model',
          text: 'No se detectó audio, intenta de nuevo.',
          timestamp: new Date(),
        }]);
        return;
      }
      setIsTranscribing(true);
      try {
        const formData = new FormData();
        formData.append('audio', result.blob, `recording.${result.mimeType.split('/')[1]?.split(';')[0] || 'webm'}`);
        const res = await fetch('/api/agent/transcribe', {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          const transcript = (data.transcript || '').trim();
          if (transcript) {
            sendMessage(transcript);
          } else {
            setMessages(prev => [...prev, {
              role: 'model',
              text: 'No se detectó audio, intenta de nuevo.',
              timestamp: new Date(),
            }]);
          }
        } else if (res.status === 429) {
          setMessages(prev => [...prev, {
            role: 'model',
            text: '⏳ El servicio de IA alcanzó su límite de solicitudes por minuto. Espera unos segundos e intenta de nuevo.',
            timestamp: new Date(),
          }]);
        } else if (res.status === 400) {
          const data = await res.json().catch(() => ({}));
          setMessages(prev => [...prev, {
            role: 'model',
            text: data.error === 'unsupported_format'
              ? 'Tu navegador grabó en un formato de audio no compatible. Intenta de nuevo.'
              : 'Ocurrió un error al procesar tu audio. Por favor intenta de nuevo.',
            timestamp: new Date(),
          }]);
        } else {
          setMessages(prev => [...prev, {
            role: 'model',
            text: 'Ocurrió un error al procesar tu audio. Por favor intenta de nuevo.',
            timestamp: new Date(),
          }]);
        }
      } catch (_e) {
        setMessages(prev => [...prev, {
          role: 'model',
          text: 'Error de conexión. Verifica tu red e intenta de nuevo.',
          timestamp: new Date(),
        }]);
      } finally {
        setIsTranscribing(false);
      }
      return;
    }

    await audioRecorder.start();
  };

  const formatText = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      const lines = part.split('\n');
      return lines.map((line, j) => (
        <span key={`${i}-${j}`}>
          {line}
          {j < lines.length - 1 && <br />}
        </span>
      ));
    });
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-110 group"
          title={`Abrir ${agentConfig.agentName}`}
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute right-16 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {agentConfig.agentName}
          </span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed z-50 flex flex-col rounded-2xl shadow-2xl border border-gray-200 bg-white overflow-hidden"
          style={{
            right: geometry.right,
            bottom: geometry.bottom,
            width: geometry.width,
            height: geometry.height,
            maxWidth: 'calc(100vw - 2rem)',
            maxHeight: 'calc(100vh - 5rem)',
          }}>
          <div
            onPointerDown={handleResizePointerDown}
            className="absolute top-0 left-0 w-4 h-4 z-10 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors"
            style={{ cursor: 'nwse-resize' }}
            title="Redimensionar"
          >
            <GripVertical className="h-3 w-3 rotate-45" />
          </div>
          {/* Header */}
          <div
            onPointerDown={handleHeaderPointerDown}
            className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <p className="font-semibold text-sm leading-none">{agentConfig.agentName}</p>
                <p className="text-xs text-blue-100 mt-0.5">Asistente QPulseMES</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMessages([])}
                title="Nueva conversación"
                className="p-1.5 rounded-full hover:bg-white/20 transition-colors text-xs text-blue-100 hover:text-white"
              >
                Nueva
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                title="Minimizar"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              <button
                onClick={() => { setIsOpen(false); setMessages([]); }}
                className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                title="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {msg.role === 'user' ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </div>
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white rounded-tr-sm'
                    : 'bg-white text-gray-800 border border-gray-200 rounded-tl-sm shadow-sm'
                }`}>
                  {formatText(msg.text)}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="h-3.5 w-3.5 text-gray-600" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1 items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested questions (only when few messages) */}
          {messages.length <= 1 && !isLoading && (
            <div className="px-4 pb-2 bg-gray-50">
              <p className="text-xs text-gray-400 mb-2">Preguntas frecuentes:</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  '¿Cómo se calcula el % Desperdicio?',
                  '¿De dónde viene el OEE?',
                  '¿Cómo funciona la Ejecución de Planta?',
                  '¿Qué permisos necesito para importar órdenes?',
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => { setInput(q); inputRef.current?.focus(); }}
                    className="text-xs bg-white border border-gray-200 rounded-full px-2.5 py-1 text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-200">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe tu pregunta..."
                rows={1}
                className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                style={{ minHeight: '38px', maxHeight: '100px' }}
                onInput={(e) => {
                  const t = e.currentTarget;
                  t.style.height = 'auto';
                  t.style.height = Math.min(t.scrollHeight, 100) + 'px';
                }}
                disabled={isLoading || audioRecorder.state === 'recording'}
              />
              {audioRecorder.state !== 'unsupported' && (
                <button
                  onClick={handleMicClick}
                  disabled={isLoading || isTranscribing || audioRecorder.state === 'requesting-permission'}
                  title={audioRecorder.state === 'recording' ? 'Detener grabación' : 'Grabar mensaje de voz'}
                  className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 disabled:cursor-not-allowed ${
                    audioRecorder.state === 'recording'
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-50'
                  }`}
                >
                  {isTranscribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : audioRecorder.state === 'recording' ? (
                    <Square className="h-3.5 w-3.5" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </button>
              )}
              {audioRecorder.state === 'recording' && (
                <span className="text-xs text-red-500 font-medium tabular-nums flex-shrink-0 self-center">
                  ● {Math.floor(audioRecorder.elapsedSeconds / 60)}:{String(audioRecorder.elapsedSeconds % 60).padStart(2, '0')}
                </span>
              )}
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || isLoading || audioRecorder.state === 'recording'}
                className="w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors flex-shrink-0"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5 text-center">Enter para enviar · Shift+Enter para nueva línea</p>
          </div>
        </div>
      )}
    </>
  );
}
